import React, { useState, useEffect, useMemo } from "react";
import { AxiosError } from "axios";
import moment from "moment";
import { showTypeSupportsJudgeSchedules } from "src/routes/Event/ManageShow/ManageShow";
import {
  IShow,
  IEvent,
  ShowRunTimeSlotDto,
  BreedGroupJudgeDto,
  BreedJudgeDto,
  IRunningOrder,
  IRunningOrderDogClass,
  IShowElementExercise,
} from "@eagerdog/interfaces";
import { Constants, Deductions, ShowTypes, getElementConfig, showTypeUsesSections, UKC_JUNIOR_FEE_SHOW_TYPES, isCwagsFeoEligible, isCwagsFeoShowWide, normalizeAKCConformationShowElement, normalizeBreedGroupValue, toUkcRallyFormat } from "@eagerdog/constants";

import { nonLicensed, helperService } from "src/services/helper.service";
import { apiService } from "src/services";

import { toast } from "src/components/Toast/ToastManager";
import StepForm, { Step, useStepForm } from "src/components/StepForm/StepForm";
import Checkbox, { ICheck } from "src/components/Checkbox/Checkbox";
import Dropdown, { States, Provinces, IOption } from "src/components/Dropdown/Dropdown";
import Input from "src/components/Input/Input";
import Phone from "src/components/Phone/Phone";

import ShowTypeTab from "./ShowTypeTab/ShowTypeTab";
import ShowNameTab from "./ShowNameTab/ShowNameTab";
import JudgesTab, { IJudge } from "./JudgesTab/JudgesTab";
import PriceTab, { IPrice } from "./PriceTab/PriceTab";
//import WaitlistTab, { IWaitlistItem } from "./WaitlistTab/WaitlistTab";
import TimeslotTab from "./TimeslotTab/TimeslotTab";

import flagIcon from "src/assets/icons/flag.svg";
import pinIcon from "src/assets/icons/pin.svg";

import { INestedShowElement, nestedElementSetupShows, shouldUseNestedSetup, getShowElementsByNested } from "./utils";
import { createUKCConformationRunningOrder } from "./utils/ukcConformationRings";
import {
  buildUkcRallyExercises,
  convertUkcRallyRingsFormat,
  finaliseUkcRallyShowElements,
  getUkcRallyShowFormat,
  isUkcRallyShowForm,
  normaliseUkcRallyShow,
} from "./utils/ukcRallyShowElements";

import "./ShowForm.scss";

interface IProps {
  onFinish(): void,
  clubId: string | undefined,
  event: IEvent | undefined,
  show?: IShow,
  currency?: string,
  onNavigateToJudgeSchedules?: () => void
}

export interface ShowRunTimeSlotDtoId extends ShowRunTimeSlotDto {
  id?: string;
  /** Inherited from ShowRunTimeSlotDto — re-declared here for clarity */
  level?: string;
}

interface IGroupedLevel {
  sanctioning_club: string,
  show_element: string,
  level: string,
  group_with: string
}

export const groupedRunningOrderLevels:IGroupedLevel[] = [
  // { sanctioning_club: Constants.sanctioning_club.AKC, show_element: Constants.show_type.obedience, level: Constants.dog_class_element_level.preferred_novice, group_with:  Constants.dog_class_element_level.beginner_novice_b },
  { sanctioning_club: Constants.sanctioning_club.AKC, show_element: Constants.show_type.obedience, level: Constants.dog_class_element_level.preferred_open, group_with:  Constants.dog_class_element_level.open_b },
  { sanctioning_club: Constants.sanctioning_club.AKC, show_element: Constants.show_type.obedience, level: Constants.dog_class_element_level.preferred_utility, group_with:  Constants.dog_class_element_level.utility_b }
];

export const isGroupedLevel = (sanctioning_club: string, show_element: string, level: string) => {
  let hasGroupedLevel:IGroupedLevel[] = groupedRunningOrderLevels.filter((gl: IGroupedLevel) => { return gl.sanctioning_club === sanctioning_club && gl.show_element === show_element && gl.level === level });

  return hasGroupedLevel.length > 0 ? hasGroupedLevel : undefined;
}

const ShowForm: React.FC<IProps> = (rawProps) => {
  // UKC Rally: the form edits the canonical class form (level RO1 + section B). A show saved
  // before the refactor (level "RO1 B") is normalised here and converted back on save, so a
  // live show keeps the format its entries already use.
  const normalisedShow = useMemo(
    () => normaliseUkcRallyShow(rawProps.show, rawProps.event?.sanctioning_club),
    [rawProps.show, rawProps.event?.sanctioning_club]
  );
  const props: IProps = normalisedShow === rawProps.show ? rawProps : { ...rawProps, show: normalisedShow };

  const [isCreatingShow, setIsCreatingShow] = useState<boolean>(false);
  const { switchToTab, activeTab } = useStepForm();

  let hasTimeslots:string[] = useMemo(() => {
    return [
      Constants.show_type.fast_cat,
      Constants.show_type.fast_dash,
      Constants.show_type.cat,
      Constants.show_type.fetch,
      Constants.show_type.obedience_show_n_go,
      Constants.show_type.general_rally,
    ];
  }, []);

  let originalShowElements:any = props.show?.show_elements ?? JSON.parse(JSON.stringify(props.show?.show_elements || []));

  // Initialize steps based on whether the show uses timeslots
  const initialSteps = useMemo(() => {
    const baseSteps = ["Show Type", "Show Name and Date", "Judges", "Price", "Secretary (optional)", "Chairperson (optional)"];
    if (props.show && hasTimeslots.includes(props.show.show_type)) {
      return [...baseSteps, props.show.show_type === Constants.show_type.fetch ? "Run Times (optional)" : "Run Times"];
    }
    return baseSteps;
  }, [props.show, hasTimeslots]);

  const [steps, setSteps] = useState<string[]>(initialSteps);

  // Show Type Tab
  const [clubName, setClubName] = useState<string>(props.show?.club_name ?? "");
  const [showType, setShowType] = useState<IOption>(props.event && props.event.sanctioning_club === Constants.sanctioning_club.UKC ? { value: Constants.show_type.nosework, id: Constants.show_type.nosework } : { value: Constants.show_type.conformation, id: Constants.show_type.conformation });
  const [showElements, setShowElements] = useState<ICheck[]>([]);
  const [showElementJudges, setShowElementJudges] = useState<ICheck[]>([]);
  const [armNum, setArmNum] = useState<number>();
  const [breedSearch, setBreedSearch] = useState<string>(props.show?.specialty_breed ?? "");
  const [splashCount, setSplashCount] = useState<number>(6);

  const [showElementsNested, setShowElementsNested] = useState<INestedShowElement[]>([]);

  // Show Name Tab
  const [akcNum, setAkcNum] = useState<string>();
  const [showName, setShowName] = useState<string>();
  const [customShowName, setCustomShowName] = useState<string>("");
  const [useCloseDate, setUseCloseDate] = useState<boolean>(false);
  const [showDate, setShowDate] = useState<Date>();
  const [closeDate, setCloseDate] = useState<Date>();
  const [isFunMatch, setIsFunMatch] = useState<boolean>(false);
  const [isTrailingShow, setIsTrailingShow] = useState<boolean>(false);
  const [previousShow, setPreviousShow] = useState<IShow>();

  // Judges Tab
  const [useSameJudge, setUseSameJudge] = useState<boolean>(true);
  const [judges, setJudges] = useState<IJudge[]>([]);
  const [breedGroups, setBreedGroups] = useState<BreedGroupJudgeDto[]>([]);
  const [bsList, setBsList] = useState<BreedJudgeDto[]>([]);

  // Price Tab
  const [useSamePrice, setUseSamePrice] = useState<boolean>(true);
  const [prices, setPrices] = useState<IPrice[]>([]);
  const [useEarlyBirdPrice, setUseEarlyBirdPrice] = useState<boolean>(false);
  const [earlyBirdEndDate, setEarlyBirdEndDate] = useState<any>();
  const [juniorFee, setJuniorFee] = useState<string>();
  const [earlyBirdJuniorFee, setEarlyBirdJuniorFee] = useState<string>();

  // C-WAGS FEO (For Exhibition Only)
  // Scent / Games: show-wide toggle from the Show Type tab. Obedience / Rally: derived from
  // whether any level has the FEO section checked in the nested tree.
  const [feoShowWideAvailable, setFeoShowWideAvailable] = useState<boolean>(props.show ? !!props.show.feo_available : true);
  const [feoFee, setFeoFee] = useState<string>(props.show?.feo_fee?.toString() ?? "");

  const isFeoEnabled = (): boolean => {
    if (!isCwagsFeoEligible(props.event?.sanctioning_club, showType.id)) {
      return false;
    }
    if (isCwagsFeoShowWide(props.event?.sanctioning_club, showType.id)) {
      return feoShowWideAvailable;
    }
    // Obedience / Rally: FEO is offered when any selected level has the FEO section selected
    return showElementsNested.some((el) =>
      el.elementSelected &&
      el.showLevels.some((lvl) =>
        lvl.levelSelected &&
        (lvl.sections ?? []).some((s) => s.section === Constants.dog_class_section.feo && s.sectionSelected)
      )
    );
  };

  // Waitlist Tab
  //const [waitlists, setWaitlists] = useState<IWaitlistItem[]>([]);

  // Timeslot Tab
  const [timeslots, setTimeslots] = useState<ShowRunTimeSlotDtoId[]>([]);
  const [originalTimeslots, setOriginalTimeslots] = useState<ShowRunTimeSlotDtoId[]>([]);

  // Secretary Tab
  const editSecretary = props.show?.contacts?.filter(
    (c) => c.contact_type === "secretary"
  )[0];

  const [secFirst, setSecFirst] = useState<string>(
    editSecretary ? editSecretary.first_name : ""
  );
  const [secLast, setSecLast] = useState<string>(
    editSecretary ? editSecretary.last_name : ""
  );
  const [secPhone, setSecPhone] = useState<string>(
    editSecretary ? editSecretary.phone : ""
  );
  const [secEmail, setSecEmail] = useState<string>(
    editSecretary ? editSecretary.email : ""
  );

  const [secretaryAddress, setSecretaryAddress] = useState<string>(
    editSecretary?.address?.line1 ?? ""
  );

  const [secretaryAddress2, setSecretaryAddress2] = useState<string>(
    editSecretary?.address?.line2 ?? ""
  );

  const [secretaryCity, setSecretaryCity] = useState<string>(
    editSecretary?.address?.city ?? ""
  );

  const [secretaryZip, setSecretaryZip] = useState<string>(
    editSecretary?.address?.postal_code ?? ""
  );

  const [secretaryCountry, setSecretaryCountry] = useState<IOption>(
    editSecretary?.address?.country
      ? { value: editSecretary.address.country, id: editSecretary.address.country }
      : { value: "USA", id: "USA" }
  );

  const [secretaryProvince, setSecretaryProvince] = useState<IOption>(
    editSecretary?.address?.state && editSecretary?.address?.country === "Canada"
      ? { value: editSecretary.address.state, id: editSecretary.address.state }
      : { value: "Alberta", id: "AB" }
  );

  const [secretaryState, setSecretaryState] = useState<IOption>(
    editSecretary?.address?.state && editSecretary?.address?.country === "USA"
      ? { value: editSecretary.address.state, id: editSecretary.address.state }
      : { value: "Alabama", id: "AL" }
  );

  // Chairperson Tab
  const editChair = props.show?.contacts?.filter(
    (c) => c.contact_type === "chairperson"
  )[0];

  const [chairFirst, setChairFirst] = useState<string>(
    editChair ? editChair.first_name : ""
  );
  const [chairLast, setChairLast] = useState<string>(
    editChair ? editChair.last_name : ""
  );
  const [chairPhone, setChairPhone] = useState<string>(
    editChair ? editChair.phone : ""
  );
  const [chairEmail, setChairEmail] = useState<string>(
    editChair ? editChair.email : ""
  );

  const [chairpersonAddress, setChairpersonAddress] = useState<string>(
    editChair?.address?.line1 ?? ""
  );

  const [chairpersonAddress2, setChairpersonAddress2] = useState<string>(
    editChair?.address?.line2 ?? ""
  );

  const [chairpersonCity, setChairpersonCity] = useState<string>(
    editChair?.address?.city ?? ""
  );

  const [chairpersonZip, setChairpersonZip] = useState<string>(
    editChair?.address?.postal_code ?? ""
  );

  const [chairpersonCountry, setChairpersonCountry] = useState<IOption>(
    editChair?.address?.country
      ? { value: editChair.address.country, id: editChair.address.country }
      : { value: "USA", id: "USA" }
  );

  const [chairpersonProvince, setChairpersonProvince] = useState<IOption>(
    editChair?.address?.state && editChair?.address?.country === "Canada"
      ? { value: editChair.address.state, id: editChair.address.state }
      : { value: "Alberta", id: "AB" }
  );

  const [chairpersonState, setChairpersonState] = useState<IOption>(
    editChair?.address?.state && editChair?.address?.country === "USA"
      ? { value: editChair.address.state, id: editChair.address.state }
      : { value: "Alabama", id: "AL" }
  );

  // Use same secretary/chairperson as event
  const [useSameSecretary, setUseSameSecretary] = useState<boolean>(!editSecretary);
  const [useSameChairperson, setUseSameChairperson] = useState<boolean>(!editChair);

  const getShowElements = () => {
    const isUKCConformationFreeClass = (level?: string) => {
      if (
        props.event?.sanctioning_club !== Constants.sanctioning_club.UKC ||
        showType.value !== Constants.show_type.conformation
      ) {
        return false;
      }

      return (
        level === Constants.dog_class_element_level.total_dog ||
        level === Constants.dog_class_element_level.total_junior
      );
    };
    
    const getDefaultJudgeInfo = () => {
      const named =
        judges.find((j: IJudge) => j.judge_element === "" && (j.judge_name || "").trim() !== "") ||
        judges.find((j: IJudge) => (j.judge_name || "").trim() !== "") ||
        judges[0];
      if (!named) {
        return undefined;
      }
      const copy: any = { ...named };
      delete copy.judge_element;
      return copy;
    };

    const getJudgeInfoByShowElement = (show_element: string) => {
      if (useSameJudge) {
        return getDefaultJudgeInfo();
      }

      const match = judges.find((j: IJudge) => j.judge_element === show_element);
      if (match && (match.judge_name || "").trim() !== "") {
        return match;
      }

      // UKC Conformation: a class without its own judge uses the default/element judge.
      // Breed-group judges are optional overrides and are stored separately.
      if (
        props.event?.sanctioning_club === Constants.sanctioning_club.UKC &&
        showType.value === Constants.show_type.conformation
      ) {
        return getDefaultJudgeInfo() || { judge_name: "N/A", judge_number: "N/A" };
      }

      return match || { judge_name: "N/A", judge_number: "N/A" };
    }

    const showElementHasPrice = (show_element: string) => {
      let p = prices.filter((_p: IPrice) => { return _p.judge_element === show_element; })
      return p.length > 0 ? p[0] : false;
    }

    const showElementHasEarlyBirdPrice = (show_element: string) => {
      let p = prices.filter((_p: IPrice) => { return (_p.judge_element === show_element) && (_p.early_bird_fee !== undefined); });
      return p.length > 0 ? p[0] : false;
    }

    /*const showElementHasMaxEntries = (show_element: string) => {
      let w = waitlists.filter((w: IWaitlistItem) => { return w.show_element === show_element && w.max_entries !== undefined; });
      return w.length > 0 ? w[0] : false;
    }

    const showElementHasWaitlist = (show_element: string) => {
      let w = waitlists.filter((w: IWaitlistItem) => { return w.show_element === show_element && w.waitlist_enabled === true; });
      return w.length > 0 ? w[0] : false;
    }*/

    const showElementHasExercises = (show_element: string) => {
      let s = props.show?.show_elements.filter((s: any) => {return s.level === show_element && s.exercises !== undefined; }) || [];
      return s.length > 0 ? s[0] : false;
    }

    // Helper function to find existing show element data to preserve settings
    const getExistingShowElement = (show_element: string, level?: string) => {
      if (!props.show?.show_elements) return null;

      return props.show.show_elements.find((se: any) => {
        // For elements with levels (like obedience classes)
        if (level) {
          return se.level === show_element || se.level === level;
        }
        // For elements without levels
        return se.show_element === show_element || se.level === show_element;
      });
    };

    const filtered = showElements.filter(e => e.checked);
    return filtered.map((s: ICheck) => {
      let _element:any = { show_element: s.label, ...getJudgeInfoByShowElement(s.label) };
      let hasPrice:any = showElementHasPrice(s.label);
      let hasEarlyBird:any = showElementHasEarlyBirdPrice(s.label);
      //let hasMaxEntries:any = showElementHasMaxEntries(s.label);
      //let hasWaitlist:any = showElementHasWaitlist(s.label);
      let hasExercises:any = showElementHasExercises(s.label);

      // Apply splash count for UKC Dock Diving Air element from current form state
      if (
        props.event?.sanctioning_club === Constants.sanctioning_club.UKC &&
        showType.id === Constants.show_type.dock_jumping &&
        s.label === Constants.dog_class_element.dock_jumping_air
      ) {
        _element.splash_count = splashCount;
      }

      // Find existing show element to preserve settings
      const existingElement = getExistingShowElement(s.label);

      // Preserve existing properties that aren't being modified by the form
      if (existingElement) {
        // Preserve order_of_exercises and other settings not handled by form
        if ((existingElement as any).order_of_exercises !== undefined) {
          _element.order_of_exercises = (existingElement as any).order_of_exercises;
        }

        // Preserve any other existing properties that might not be in the form
        Object.keys(existingElement).forEach(key => {
          if (!_element.hasOwnProperty(key) &&
              key !== '_id' &&
              key !== '__v' &&
              key !== 'created_at' &&
              key !== 'updated_at') {
            _element[key] = (existingElement as any)[key];
          }
        });
      }

      if (hasPrice) {
        _element.fee = hasPrice.fee ? Number(hasPrice.fee) : "";
      }

      if (hasEarlyBird) {
        _element.early_bird_fee = Number(hasEarlyBird.early_bird_fee);
      }

      _element.max_entries = 500;

      /*if (hasMaxEntries) {
        _element.max_entries = Number(hasMaxEntries.max_entries);
      }*/

      /*if (hasWaitlist) {
        _element.waitlist_enabled = hasWaitlist.waitlist_enabled;
        _element.waitlist_size = Number(hasWaitlist.waitlist_size);
      } else {*/
        _element.waitlist_enabled = false;
      //}

      if (Constants.show_type.fetch === showType.value) {
        _element.use_judge_name = showElementJudges.find((elementJudge) => elementJudge.label === s.label)?.checked ?? false;
      }

      if (props.event && props.event.sanctioning_club === Constants.sanctioning_club.UKC && (showType.value === Constants.show_type.obedience || showType.value === Constants.show_type.rally_obedience)) {
        const isRallyObedience = showType.value === Constants.show_type.rally_obedience && props.event.sanctioning_club === Constants.sanctioning_club.UKC;

        const rallyExercises = isRallyObedience
          ? buildUkcRallyExercises(s.label, hasExercises?.exercises)
          : undefined;

        if (rallyExercises) {
          // UKC Rally: full pool of eligible signs (current level + all lower levels) merged
          // with the saved exercises so enabled/disabled/order state survives.
          _element.exercises = rallyExercises;
        } else if (hasExercises && hasExercises.exercises.length > 0) {
          _element.exercises = hasExercises.exercises;
        } else if (props.event && Deductions[props.event.sanctioning_club]?.[showType.value]?.[s.label] !== undefined) {
          const deductionDefinitions: any[] = Deductions[props.event.sanctioning_club][showType.value][s.label]["I"];

          if (deductionDefinitions?.length > 0) {
            _element.exercises = [];

            for (let d in deductionDefinitions) {
              let exercise: IShowElementExercise = {
                exercise: deductionDefinitions[d].exercise,
                disabled: false,
              };

              if (deductionDefinitions[d].sign !== undefined) {
                exercise.sign = deductionDefinitions[d].sign;
              }

              _element.exercises.push(exercise);
            }
          }
        }
      }

      if (
        ((showType.value === Constants.show_type.rally_obedience || showType.value === Constants.show_type.obedience)) ||
        (props.event?.sanctioning_club === Constants.sanctioning_club.C_WAGS && showType.value === Constants.show_type.scent) ||
        (props.event?.sanctioning_club === Constants.sanctioning_club.AKC && showType.value === Constants.show_type.lure_coursing) ||
        (props.event?.sanctioning_club === Constants.sanctioning_club.UKC && showType.value === Constants.show_type.shed_dog) ||
        //(props.event?.sanctioning_club === Constants.sanctioning_club.UKC && showType.value === Constants.show_type.dock_jumping) ||
        (props.event?.sanctioning_club === Constants.sanctioning_club.AKC && (showType.value === Constants.show_type.fetch)) ||
        (props.event?.sanctioning_club === Constants.sanctioning_club.PWDCA && showType.value === Constants.show_type.water_dog) ||
        (props.event?.sanctioning_club === Constants.sanctioning_club.AKC && showType.value === Constants.show_type.canine_good_citizen) ||
        (props.event?.sanctioning_club === Constants.sanctioning_club.AKC && showType.value === Constants.show_type.trick_dog) ||
        (props.event?.sanctioning_club === Constants.sanctioning_club.ABI && showType.value === Constants.show_type.country_rat_race) ||
        (props.event?.sanctioning_club === Constants.sanctioning_club.ABI && showType.value === Constants.show_type.scent_dog_games) ||
        (props.event?.sanctioning_club === Constants.sanctioning_club.UKC && showType.value === Constants.show_type.agility) ||
        (props.event?.sanctioning_club === Constants.sanctioning_club.BHA && showType.value === Constants.show_type.barn_hunt)
      ) {
        _element.show_element = showType.value;
        _element.level = s.label;
      }

      // Define non-licensed classes for Conformation (AKC, UKC, etc.)
      let nonLicensed:string[] = [
        // AKC classes
        Constants.dog_class_element_level.akc_sled_dog,
        Constants.dog_class_element_level.akc_teams,
        Constants.dog_class_element_level.akc_parade_of_titleholders,
        Constants.dog_class_element_level.akc_coursing,
        Constants.dog_class_element_level.akc_futurity,
        Constants.dog_class_element_level.akc_best_head,
        Constants.dog_class_element_level.akc_best_movement,
        Constants.dog_class_element_level.akc_best_field,
        Constants.dog_class_element_level.akc_hunting_class,
        Constants.dog_class_element_level.akc_field_trial_class,
        // Puppy Sweepstakes
        Constants.dog_class_element_level.akc_puppy_sweepstakes_6_9,
        Constants.dog_class_element_level.akc_puppy_sweepstakes_9_12,
        Constants.dog_class_element_level.akc_puppy_sweepstakes_12_18,
        Constants.dog_class_element_level.akc_puppy_sweepstakes_12_15,
        Constants.dog_class_element_level.akc_puppy_sweepstakes_15_18,
        Constants.dog_class_element_level.akc_puppy_sweepstakes_18_24,
        Constants.dog_class_element_level.akc_puppy_sweepstakes_bred_by_exhibitor_6_18,
        // Veteran Sweepstakes
        Constants.dog_class_element_level.akc_veteran_sweepstakes_7_9,
        Constants.dog_class_element_level.akc_veteran_sweepstakes_9_plus,
        Constants.dog_class_element_level.akc_veteran_sweepstakes_7_10,
        Constants.dog_class_element_level.akc_veteran_sweepstakes_8_10,
        Constants.dog_class_element_level.akc_veteran_sweepstakes_8_11,
        Constants.dog_class_element_level.akc_veteran_sweepstakes_10_12,
        Constants.dog_class_element_level.akc_veteran_sweepstakes_11_plus,
        Constants.dog_class_element_level.akc_veteran_sweepstakes_10_13,
        Constants.dog_class_element_level.akc_veteran_sweepstakes_10_plus,
        Constants.dog_class_element_level.akc_veteran_sweepstakes_12_plus,
        Constants.dog_class_element_level.akc_veteran_sweepstakes_13_plus,
        // Dual Title Sweepstakes
        Constants.dog_class_element_level.akc_dual_title_sweepstakes_6m_7y,
        Constants.dog_class_element_level.akc_dual_title_sweepstakes_7y_plus,
        Constants.dog_class_element_level.akc_dual_title_sweepstakes_7y_11y,
        Constants.dog_class_element_level.akc_dual_title_sweepstakes_10y_plus,
        Constants.dog_class_element_level.akc_dual_title_sweepstakes_11y_plus,
        // Junior Showmanship classes (all variations)
        Constants.dog_class_element_level.akc_novice_junior,
        Constants.dog_class_element_level.akc_novice_intermediate,
        Constants.dog_class_element_level.akc_novice_senior,
        Constants.dog_class_element_level.akc_open_junior,
        Constants.dog_class_element_level.akc_open_intermediate,
        Constants.dog_class_element_level.akc_open_senior,
        Constants.dog_class_element_level.akc_master_class,
        Constants.dog_class_element_level.akc_junior_showmanship_novice,
        Constants.dog_class_element_level.akc_junior_showmanship_open,
        Constants.dog_class_element_level.akc_junior_showmanship_master,
        // UKC non-licensed classes
        Constants.dog_class_element.novice_puppy,
        Constants.dog_class_element.veteran,
        Constants.dog_class_element.brood_bitch_stud_dog,
        Constants.dog_class_element.brace,
        Constants.dog_class_element.total_dog,
        Constants.dog_class_element.total_junior,
        Constants.dog_class_element.other_racing_bred_dogs,
      ];

      // Define Junior Showmanship classes specifically
      let juniorShowmanship:string[] = [
        Constants.dog_class_element_level.akc_novice_junior,
        Constants.dog_class_element_level.akc_novice_intermediate,
        Constants.dog_class_element_level.akc_novice_senior,
        Constants.dog_class_element_level.akc_open_junior,
        Constants.dog_class_element_level.akc_open_intermediate,
        Constants.dog_class_element_level.akc_open_senior,
        Constants.dog_class_element_level.akc_master_class,
        Constants.dog_class_element_level.akc_junior_showmanship_novice,
        Constants.dog_class_element_level.akc_junior_showmanship_open,
        Constants.dog_class_element_level.akc_junior_showmanship_master,
      ];

      // For AKC Conformation: Regular classes get show_element="Conformation", level=class_name
      // BUT: Skip non-licensed classes (they're handled below)
      if ((props.event?.sanctioning_club === Constants.sanctioning_club.AKC && showType.value === Constants.show_type.conformation) && !nonLicensed.includes(s.label)) {
        _element.show_element = showType.value;
        _element.level = s.label;
      }

      // Handle ABI Conformation - all options should be stored as levels under "Conformation" show_element
      if (props.event?.sanctioning_club === Constants.sanctioning_club.ABI && showType.value === Constants.show_type.conformation) {
        _element.show_element = showType.value;
        _element.level = s.label;
      }

      if ((props.event?.sanctioning_club === Constants.sanctioning_club.UKC && showType.value === Constants.show_type.precision_coursing)) {
        if (nonLicensed.includes(s.label)) {
          _element.show_element = Constants.dog_class_element.conformation_non_licensed;
          _element.level = s.label;
        } else {
          _element.show_element = showType.value;
          _element.level = s.label;
        }
      }

      let obedNonLicensed: string[] = [
        Constants.dog_class_element_level.brace,
        Constants.dog_class_element_level.pairs,
        Constants.dog_class_element_level.precision_heeling,
        Constants.dog_class_element_level.team,
        Constants.dog_class_element_level.veteran
      ];

      if ((props.event?.sanctioning_club === Constants.sanctioning_club.UKC && showType.value === Constants.show_type.obedience)) {
        if (obedNonLicensed.includes(s.label)) {
          _element.show_element = Constants.dog_class_element.performance_non_licensed_obedience;
          _element.level = s.label;
        }
      }

      // CKC Obedience: unofficial classes (Chapter 16) are stored under one element, like UKC non-licensed
      if (props.event?.sanctioning_club === Constants.sanctioning_club.CKC && showType.value === Constants.show_type.obedience) {
        const ckcUnofficial: string[] = ShowTypes[Constants.sanctioning_club.CKC]?.[Constants.show_type.obedience]?.[Constants.dog_class_element.obedience_unofficial] || [];
        if (ckcUnofficial.includes(s.label)) {
          _element.show_element = Constants.dog_class_element.obedience_unofficial;
          _element.level = s.label;
        }
      }

      // CKC Rally Obedience: unofficial classes (Chapter 19: Brace, Team) are stored under one element
      if (props.event?.sanctioning_club === Constants.sanctioning_club.CKC && showType.value === Constants.show_type.rally_obedience) {
        const ckcRallyUnofficial: string[] = ShowTypes[Constants.sanctioning_club.CKC]?.[Constants.show_type.rally_obedience]?.[Constants.dog_class_element.rally_unofficial] || [];
        if (ckcRallyUnofficial.includes(s.label)) {
          _element.show_element = Constants.dog_class_element.rally_unofficial;
          _element.level = s.label;
        }
      }

      if (showType.value === Constants.show_type.conformation && nonLicensed.includes(s.label)) {
        _element.show_element = /*props.event?.sanctioning_club === Constants.sanctioning_club.ABI ?  Constants.dog_class_element.conformation : */Constants.dog_class_element.conformation_non_licensed;

        if (props.event?.sanctioning_club === Constants.sanctioning_club.ABI || props.event?.sanctioning_club === Constants.sanctioning_club.ISWS) {
          _element.level = s.label;
        }
      }

      /*if (props.event?.sanctioning_club === Constants.sanctioning_club.ABI && showType.value === Constants.show_type.conformation) {
        if (s.label === Constants.dog_class_element_level.abi_brace || s.label === Constants.dog_class_element_level.abi_veteran) {
          _element.show_element = showType.value;
          _element.level = s.label;
        }
      }*/


      if (props.event?.sanctioning_club !== Constants.sanctioning_club.ABI && props.event?.sanctioning_club !== Constants.sanctioning_club.ISWS && showType.value === Constants.show_type.conformation && nonLicensed.includes(s.label)) {
        // Check if this is a Junior Showmanship class
        if (juniorShowmanship.includes(s.label)) {
          _element.show_element = Constants.dog_class_element.junior_showmanship;
          _element.level = s.label;
        } else {
          // Regular non-licensed class (Sled Dog, Coursing, etc.)
          _element.show_element = Constants.dog_class_element.conformation_non_licensed;
          _element.level = s.label;
        }
      }

      // UKC Conformation: Total Dog and Total Junior must always be free.
      if (isUKCConformationFreeClass(_element.level)) {
        _element.fee = 0;
        _element.early_bird_fee = 0;
      }

      return _element;
    });
  }

  const popToast = (msg: string) => {
    toast.show({
      title: "Create/Edit Show",
      content: msg,
      duration: 10000,
      type: "fail"
    });
  }

  const validateShow = (p: any) => {
    const isJudgeEmpty = (j: any) => {
      const name = (j?.judge_name || "").trim();
      const number = (j?.judge_number || "").trim();
      // For PWDCA and C-Wags, judge_number is optional, only judge_name is required
      if (props.event?.sanctioning_club === Constants.sanctioning_club.PWDCA || 
          props.event?.sanctioning_club === Constants.sanctioning_club.C_WAGS) {
        return name === "";
      }
      // For other sanctioning clubs, both judge_name and judge_number are required
      return name === "" && number === "";
    }

    const isPriceEmpty = (p: any) => {
      return isNaN(parseInt(p.fee)) || p.fee === undefined || p.fee === "";
    }

    const isShowElementChecked = (elementLabel: string) => {
      // For nested shows, we check all elements
      if (shouldUseNestedSetup(p.show_type, props.event?.sanctioning_club || '')) {
        return true;
      }
      // For non-nested shows, check if the element is in the checked list
      const checkedElements = showElements.filter((se: ICheck) => se.checked);
      
      return checkedElements.some((ce: ICheck) => ce.label === elementLabel);
    }

    if (showName === "Select a Show Name") {
      popToast("Please select a show name");
      switchToTab(1);
      return false;
    }

    if (p.show_elements.length === 0) {
      popToast("Please select atleast one element for your show");
      switchToTab(0);
      return false;
    }

    if (props.event?.sanctioning_club === Constants.sanctioning_club.AKC &&
        p.show_type !== Constants.show_type.canine_good_citizen &&
        p.show_type !== Constants.show_type.trick_dog &&
        !isFunMatch &&
        (!akcNum || akcNum.trim().length === 0)) {
      popToast("Please enter an AKC number for your show");
      switchToTab(1);
      return false;
    }

    if (props.event?.sanctioning_club === Constants.sanctioning_club.AKC && (p.arm_number_start === undefined || p.arm_number_start.length === 0)) {
      popToast("Please select a starting number for your armbands");
      switchToTab(0);
      return false;
    }

    if (hasTimeslots.includes(p.show_type) && p.time_slots && p.time_slots.length === 0) {
      // Timeslots are optional — no longer block saving without them
    }

    if (p.use_early_bird_fee && moment(p.early_bird_end_date).format("X") > moment(p.show_date).format("X") && !p.trailing_show) {
      popToast("Your early bird end date cannot be after the show start date");
      switchToTab(4);
      return false;
    }

    // Validate splash count for UKC Dock Jumping Air element
    if (props.event?.sanctioning_club === Constants.sanctioning_club.UKC &&
        p.show_type === Constants.show_type.dock_jumping) {
      const airElement = p.show_elements.find((element: any) =>
        element.show_element === Constants.dog_class_element.dock_jumping_air
      );
      if (airElement && airElement.splash_count &&
          (airElement.splash_count < 1 || airElement.splash_count > 6)) {
        popToast("Number of splashes must be between 1 and 6");
        switchToTab(0);
        return false;
      }
    }

    if (p.trailing_show && previousShow === undefined) {
      popToast("Please select a previous show that your current show is trailing");
      switchToTab(1);
      return false;
    }

    // When editing an existing show that uses Judge Schedules, judge assignments
    // are managed in the Judge Schedules tab (not here), so skip judge presence
    // validation. Price validation still applies.
    const isEditingWithJudgeSchedules = !!props.show &&
      showTypeSupportsJudgeSchedules(props.event?.sanctioning_club || '', p.show_type);

    // UKC Conformation: when "Total Dog" is offered, "Total Junior" is judged
    // as part of that same overall comparison and doesn't need its own judge.
    const isTotalDogSelected = p.show_elements.some((se: any) => {
      const label = se.level || se.show_element;
      if (label !== Constants.dog_class_element_level.total_dog) {
        return false;
      }
      return shouldUseNestedSetup(p.show_type, props.event?.sanctioning_club || '') ||
        isShowElementChecked(label);
    });

    for (let i in p.show_elements) {
      // For non-nested shows, check if this element is actually selected
      const elementLabel = p.show_elements[i].level || p.show_elements[i].show_element;
      const isElementSelected = shouldUseNestedSetup(p.show_type, props.event?.sanctioning_club || '') || 
                                isShowElementChecked(elementLabel);
      
      // Only validate selected elements
      if (!isElementSelected) {
        continue;
      }

      // Total Dog doesn't have a judge — skip judge validation for it
      const isTotalDog = elementLabel === Constants.dog_class_element_level.total_dog;
      // Total Junior doesn't need its own judge when Total Dog is also offered
      const isTotalJunior = elementLabel === Constants.dog_class_element_level.total_junior;
      const skipJudgeValidation = isTotalDog || (isTotalJunior && isTotalDogSelected);

      // UKC Conformation: breed-group / per-class judges are optional. A class without
      // its own judge uses the default element judge — don't block save for the blanks.
      const isUkcConformation =
        props.event?.sanctioning_club === Constants.sanctioning_club.UKC &&
        p.show_type === Constants.show_type.conformation;
      const hasDefaultUkcJudge = isUkcConformation && judges.some(
        (j: IJudge) => (j.judge_name || "").trim() !== ""
      );

      if (!skipJudgeValidation && !isEditingWithJudgeSchedules && isJudgeEmpty(p.show_elements[i]) && !hasDefaultUkcJudge) {
        switchToTab(2);

        if (useSameJudge) {
          popToast("Please enter element judge information for your show");
        } else {
          popToast("Please enter element judge information for all show types");
        }

        return false;
      }

      if (!useSamePrice) {
        if (isPriceEmpty(p.show_elements[i])) {
          switchToTab(3);
          popToast("Please enter a price for all show types");
          return false;
        }
      } else {
        if (isPriceEmpty(p)) {
          switchToTab(3);
          popToast("Please enter a price for your show");
          return false;
        }
      }
    }

    // C-WAGS FEO: when FEO is offered a flat FEO price is required
    if (isFeoEnabled() && (feoFee === undefined || feoFee.trim() === "" || isNaN(parseFloat(feoFee)) || parseFloat(feoFee) < 0)) {
      switchToTab(3);
      popToast("Please enter an FEO price for your show");
      return false;
    }

    return true;
  }

  const createRunningOrder = (elementsToUse?: any[]) => {
    // Only use elementsToUse if explicitly passed (for C-WAGS and other nested element shows)
    // Otherwise, always use showElements state (checkboxes) for AKC/UKC standard shows
    
    let _running_order:IRunningOrder[] = [];

    // For nested setup shows (e.g. UKC Nosework, AKC Scentwork), availability is determined
    // from the passed show_elements; for flat checkbox shows, use the showElements state.
    const isElementAvailableInShow = (elementName: string): boolean => {
      if (elementsToUse) {
        return elementsToUse.some((se: any) => se.show_element === elementName || se.level === elementName);
      }
      return showElements.some((se: any) => se.label === elementName && se.checked === true);
    };

    // Used in multiple-judge path for UKC Nosework (judge_element as show_element)
    const requiresSeparateRunningOrderPerElement = props.event?.sanctioning_club === Constants.sanctioning_club.UKC &&
                                                     showType.id === Constants.show_type.nosework;

    // Using Same Judge
    if (judges.length === 1 && judges[0].judge_element.length === 0) {
      // Use elementsToUse ONLY if explicitly passed (for nested element shows like C-WAGS)
      // Otherwise use checkbox state (for AKC/UKC standard shows)
      const elementsToIterate = elementsToUse || showElements.filter((se: any) => se.checked === true);
      
      // One running order item per judge with all elements (fixes UKC Nosework duplicate judge: was one item per element)
      let _running_order_item: IRunningOrder = {
          //type: "dog_class",
          judge_name: judges[0].judge_name,
          judge_number: judges[0].judge_number,
          dog_classes: []
        };
        
        for (let se of elementsToIterate) {
          // Handle both formats: state format has 'label', show_elements format has 'level'
          const isShowElementFormat = 'show_element' in se;
          
          // For Obedience Show N Go, use the element constant to match FormBuilder-stored dog classes
          // For General Rally, use the rally element constant similarly
          const showElementForDc = (!isShowElementFormat && showType.id === Constants.show_type.obedience_show_n_go)
            ? Constants.dog_class_element.performance_obedience
            : (!isShowElementFormat && showType.id === Constants.show_type.general_rally)
            ? Constants.dog_class_element.performance_rally_obedience
            : (isShowElementFormat ? se.show_element : showType.id);

          let dc: IRunningOrderDogClass = {
            type: Constants.running_order_dog_class_type.dog_class,
            show_element: showElementForDc,
            level: isShowElementFormat ? se.level : se.label
          };
          
          // Add section if present (C-WAGS Obedience/Rally, UKC Nosework)
          if (isShowElementFormat && se.section) {
            dc.section = se.section;
          }
          

          // Check if this element has nested levels in ShowTypes that need to be expanded
          const levelKey = isShowElementFormat ? se.level : se.label;
          const hasNestedLevels = props.event && 
                                  levelKey &&
                                  ShowTypes[props.event?.sanctioning_club]?.[showType.id]?.[levelKey] !== undefined && 
                                  ShowTypes[props.event?.sanctioning_club][showType.id][levelKey].length > 0;

          if (hasNestedLevels && levelKey && props.event) {
            // Expand nested levels (e.g., AKC/UKC Obedience non-licensed classes)
            for (let level in ShowTypes[props.event.sanctioning_club][showType.id][levelKey]) {
              let levelDc:any = {
                type: Constants.running_order_dog_class_type.dog_class,
                show_element: levelKey,
                level: ShowTypes[props.event.sanctioning_club][showType.id][levelKey][level],
              };

              _running_order_item.dog_classes.push(levelDc);
            }
          } else if (isShowElementFormat) {
            // UKC Nosework (and similar): show_elements have show_element only - expand to level+section
            // so the Judge Schedule has proper structure for entry counts and durations
            // Skip expansion when element already has level (e.g. C-WAGS from nested setup)
            const elementName = se.show_element;
            const usesSections = props.event && showTypeUsesSections(props.event.sanctioning_club, showType.id);
            const elementConfig = props.event && getElementConfig(props.event.sanctioning_club, showType.id, elementName);
            const levels = elementConfig?.levels || [];
            const sections = (usesSections && elementConfig?.sections?.length) ? elementConfig.sections : [''];
            const needsExpansion = !se.level && levels.length > 0;
            if (needsExpansion) {
              levels.forEach((level: string) => {
                sections.forEach((section: string) => {
                  const levelDc: IRunningOrderDogClass = {
                    type: Constants.running_order_dog_class_type.dog_class,
                    show_element: elementName,
                    level,
                    ...(section ? { section } : {}),
                  };
                  _running_order_item.dog_classes.push(levelDc);
                });
              });
            } else {
              _running_order_item.dog_classes.push(dc);
            }
          } else {
            if (props.event) {
              let hasGroupedLevel = isGroupedLevel(props.event.sanctioning_club, showType.id, se.label);

              if (hasGroupedLevel !== undefined && hasGroupedLevel.length > 0) {
                dc.group_with = hasGroupedLevel[0].group_with;
              }
            }

            let nonLicensed:string[] = [
              Constants.dog_class_element_level.akc_sled_dog,
              Constants.dog_class_element_level.akc_teams,
              Constants.dog_class_element_level.akc_parade_of_titleholders,
              Constants.dog_class_element_level.akc_coursing,
              Constants.dog_class_element_level.akc_best_head,
              Constants.dog_class_element_level.akc_best_movement,
              Constants.dog_class_element_level.akc_best_field,
              // Junior Showmanship classes (all variations)
              Constants.dog_class_element_level.akc_novice_junior,
              Constants.dog_class_element_level.akc_novice_intermediate,
              Constants.dog_class_element_level.akc_novice_senior,
              Constants.dog_class_element_level.akc_open_junior,
              Constants.dog_class_element_level.akc_open_intermediate,
              Constants.dog_class_element_level.akc_open_senior,
              Constants.dog_class_element_level.akc_master_class,
              Constants.dog_class_element_level.akc_junior_showmanship_novice,
              Constants.dog_class_element_level.akc_junior_showmanship_open,
              Constants.dog_class_element_level.akc_junior_showmanship_master,
            ];

            if (dc.level && nonLicensed.includes(dc.level)) {
              dc.show_element = Constants.dog_class_element.conformation_non_licensed;
            } else if (dc.level === Constants.dog_class_element.scentwork_detective) {
              dc.show_element = Constants.dog_class_element.scentwork_detective;
              dc.level = Constants.dog_class_element_level.scentwork_detective;
            }
            
            // Barn Hunt classes should all have "Barn Hunt" as show_element
            if (props.event?.sanctioning_club === Constants.sanctioning_club.BHA && 
                showType.value === Constants.show_type.barn_hunt) {
              dc.show_element = Constants.dog_class_element.barn_hunt;
            }

            _running_order_item.dog_classes.push(dc);
          }
        }

        _running_order.push(_running_order_item);
    } else {
      for(let j in judges) {
        let hasJudge = _running_order.filter((r: IRunningOrder) => { return (r.judge_name === judges[j].judge_name && r.judge_number === judges[j].judge_number); });
        let showElementIsAvailable = isElementAvailableInShow(judges[j].judge_element);

        // Nested setup (AKC Scentwork, C-WAGS, ...) has one judge row per element + level
        // (+ section). Expanding the element's full level list here would add every level once
        // per row and duplicate each class once per level.
        // UKC Nosework starts with element-only tiles; ring sync expands them to level + section.
        if (judges[j].judge_level) {
          if (showElementIsAvailable) {
            const levelDc: IRunningOrderDogClass = requiresSeparateRunningOrderPerElement
              ? {
                  type: Constants.running_order_dog_class_type.dog_class,
                  show_element: judges[j].judge_element,
                }
              : {
                  type: Constants.running_order_dog_class_type.dog_class,
                  show_element: judges[j].judge_element,
                  level: judges[j].judge_level,
                  ...(judges[j].judge_section ? { section: judges[j].judge_section } : {}),
                };
            if (hasJudge.length > 0) {
              const alreadyAdded = hasJudge[0].dog_classes.some((dc: IRunningOrderDogClass) =>
                dc.show_element === levelDc.show_element &&
                (dc.level || "") === (levelDc.level || "") &&
                (dc.section || "") === (levelDc.section || "")
              );
              if (!alreadyAdded) hasJudge[0].dog_classes.push(levelDc);
            } else {
              _running_order.push({
                judge_name: judges[j].judge_name,
                judge_number: judges[j].judge_number,
                dog_classes: [levelDc]
              });
            }
          }
          continue;
        }

        // For UKC Nosework, use judge_element as show_element (Container, Exterior, etc.) with no level
        // For other shows, use showType.id as show_element and judge_element as level
        const isUKCNosework = requiresSeparateRunningOrderPerElement;

        // Obedience Show N Go and General Rally are "flat" show types whose showType.id
        // ("Obedience Show N Go" / "Rally Show N Go") is NOT a valid dog_class_element enum
        // value — the actual show_element stored on dog classes (and matched by FormBuilder)
        // is the constant "Obedience"/"Rally Obedience". Every other show type here happens to
        // have a showType.id that already matches a valid dog_class_element (e.g. "Barn Hunt",
        // "On Leash Run Through"), which is why this mapping was missed here even though the
        // single-judge path above already handles it — using showType.id directly for these two
        // fails Mongoose's show_element enum validation when creating the show.
        const showElementForDc = showType.id === Constants.show_type.obedience_show_n_go
          ? Constants.dog_class_element.performance_obedience
          : showType.id === Constants.show_type.general_rally
          ? Constants.dog_class_element.performance_rally_obedience
          : showType.id;

        let dc: IRunningOrderDogClass = {
          type: Constants.running_order_dog_class_type.dog_class,
          show_element: isUKCNosework ? judges[j].judge_element : showElementForDc,
          level: isUKCNosework ? undefined : judges[j].judge_element
        };

        if (props.event && !isUKCNosework) {
          let hasGroupedLevel = isGroupedLevel(props.event.sanctioning_club, showType.id, judges[j].judge_element);

          if (hasGroupedLevel !== undefined && hasGroupedLevel.length > 0) {
            dc.group_with = hasGroupedLevel[0].group_with;
          }
        }

        let nonLicensed:string[] = [
          Constants.dog_class_element_level.akc_sled_dog,
          Constants.dog_class_element_level.akc_teams,
          Constants.dog_class_element_level.akc_parade_of_titleholders,
          Constants.dog_class_element_level.akc_coursing,
          Constants.dog_class_element_level.akc_best_head,
          Constants.dog_class_element_level.akc_best_movement,
          Constants.dog_class_element_level.akc_best_field,
          // Junior Showmanship classes (all variations)
          Constants.dog_class_element_level.akc_novice_junior,
          Constants.dog_class_element_level.akc_novice_intermediate,
          Constants.dog_class_element_level.akc_novice_senior,
          Constants.dog_class_element_level.akc_open_junior,
          Constants.dog_class_element_level.akc_open_intermediate,
          Constants.dog_class_element_level.akc_open_senior,
          Constants.dog_class_element_level.akc_master_class,
          Constants.dog_class_element_level.akc_junior_showmanship_novice,
          Constants.dog_class_element_level.akc_junior_showmanship_open,
          Constants.dog_class_element_level.akc_junior_showmanship_master,
        ];

        if (props.event && ShowTypes[props.event?.sanctioning_club][showType.id][judges[j].judge_element] !== undefined && ShowTypes[props.event?.sanctioning_club][showType.id][judges[j].judge_element].length > 0) {
          for (let level in ShowTypes[props.event?.sanctioning_club][showType.id][judges[j].judge_element]) {
            let _hasJudge = _running_order.filter((r: IRunningOrder) => { return (r.judge_name === judges[j].judge_name && r.judge_number === judges[j].judge_number); });
            const _showElementIsAvailable = isElementAvailableInShow(judges[j].judge_element);

            let levelDc:any = {
              type: Constants.running_order_dog_class_type.dog_class,
              show_element: judges[j].judge_element,
              level: ShowTypes[props.event?.sanctioning_club][showType.id][judges[j].judge_element][level],
            };

            if (_showElementIsAvailable) {
              if (_hasJudge.length > 0) {
                _hasJudge[0].dog_classes.push(levelDc);
              } else {
                _running_order.push({
                  judge_name: judges[j].judge_name,
                  judge_number: judges[j].judge_number,
                  dog_classes: [levelDc]
                });
              }
            }
          }
        } else if (showElementIsAvailable) {
          if (nonLicensed.includes(judges[j].judge_element)) {
            dc.show_element = Constants.dog_class_element.conformation_non_licensed;
            delete dc.level;
          } else if (judges[j].judge_element === Constants.dog_class_element.scentwork_detective) {
            dc.show_element = Constants.dog_class_element.scentwork_detective;
            dc.level = Constants.dog_class_element_level.scentwork_detective;
          }

          if (hasJudge.length > 0) {
            hasJudge[0].dog_classes.push(dc);
          } else {
            _running_order.push({
              judge_name: judges[j].judge_name,
              judge_number: judges[j].judge_number,
              dog_classes: [dc]
            });
          }
        }
      }
    }

    return _running_order;
  }

  // Helper function to get the order priority for AKC Conformation classes
  const getAKCConformationClassOrder = (level: string): number => {
    // Define the complete ordering array - classes will be sorted by their position in this array
    const classOrder = [
      // Junior Showmanship (all variations - usually first)
      Constants.dog_class_element_level.akc_novice_junior,
      Constants.dog_class_element_level.akc_novice_intermediate,
      Constants.dog_class_element_level.akc_novice_senior,
      Constants.dog_class_element_level.akc_junior_showmanship_novice,
      Constants.dog_class_element_level.akc_open_junior,
      Constants.dog_class_element_level.akc_open_intermediate,
      Constants.dog_class_element_level.akc_open_senior,
      Constants.dog_class_element_level.akc_junior_showmanship_open,
      Constants.dog_class_element_level.akc_master_class,
      Constants.dog_class_element_level.akc_junior_showmanship_master,

      // 1. Puppy Sweepstakes (by age order)
      Constants.dog_class_element_level.akc_puppy_sweepstakes_6_9,
      Constants.dog_class_element_level.akc_puppy_sweepstakes_9_12,
      Constants.dog_class_element_level.akc_puppy_sweepstakes_12_18,
      Constants.dog_class_element_level.akc_puppy_sweepstakes_12_15,
      Constants.dog_class_element_level.akc_puppy_sweepstakes_15_18,
      Constants.dog_class_element_level.akc_puppy_sweepstakes_18_24,
      Constants.dog_class_element_level.akc_puppy_sweepstakes, // deprecated fallback

      // 2. Veteran Sweepstakes (by age order)
      Constants.dog_class_element_level.akc_veteran_sweepstakes_7_10,
      Constants.dog_class_element_level.akc_veteran_sweepstakes_8_10,
      Constants.dog_class_element_level.akc_veteran_sweepstakes_8_11,
      Constants.dog_class_element_level.akc_veteran_sweepstakes_10_12,
      Constants.dog_class_element_level.akc_veteran_sweepstakes_11_plus,
      Constants.dog_class_element_level.akc_veteran_sweepstakes_10_13,
      Constants.dog_class_element_level.akc_veteran_sweepstakes_12_plus,
      Constants.dog_class_element_level.akc_veteran_sweepstakes_13_plus,

      // 3. Dual-Titled Sweepstakes (by age order)
      Constants.dog_class_element_level.akc_dual_title_sweepstakes_6m_7y,
      Constants.dog_class_element_level.akc_dual_title_sweepstakes_7y_plus,
      Constants.dog_class_element_level.akc_dual_title_sweepstakes_7y_11y,
      Constants.dog_class_element_level.akc_dual_title_sweepstakes_11y_plus,

      // 4. Regular Classes (by traditional conformation order)
      Constants.dog_class_element_level.akc_4_6_month_puppy,
      Constants.dog_class_element_level.akc_4_6_month_puppy_exhibitor_only,
      Constants.dog_class_element_level.akc_6_9_month_puppy,
      Constants.dog_class_element_level.akc_9_12_month_puppy,
      Constants.dog_class_element_level.akc_12_18_month_puppy,
      Constants.dog_class_element_level.akc_18_24_month_puppy,
      Constants.dog_class_element_level.akc_novice,
      Constants.dog_class_element_level.akc_amateur_owner_handler,
      Constants.dog_class_element_level.akc_under_12_month_bred_by_exhibitor,
      Constants.dog_class_element_level.akc_adult_bred_by_exhibitor,
      Constants.dog_class_element_level.akc_american_bred,
      Constants.dog_class_element_level.akc_open,
      Constants.dog_class_element_level.akc_miscellaneous,

      // 5. Veteran Classes (by age order)
      Constants.dog_class_element_level.akc_veteran_6_8_years,
      Constants.dog_class_element_level.akc_veteran_6_plus_years,
      Constants.dog_class_element_level.akc_veteran_7_plus_years,
      Constants.dog_class_element_level.akc_veteran_7_9_years,
      Constants.dog_class_element_level.akc_veteran_7_10_years,
      Constants.dog_class_element_level.akc_veteran_8_10_years,
      Constants.dog_class_element_level.akc_veteran_8_11_years,
      Constants.dog_class_element_level.akc_veteran_8_plus_years,
      Constants.dog_class_element_level.akc_veteran_9_plus_years,
      Constants.dog_class_element_level.akc_veteran_10_12_years,
      Constants.dog_class_element_level.akc_veteran_11_plus_years,
      Constants.dog_class_element_level.akc_veteran_12_plus_years,
      Constants.dog_class_element_level.akc_veteran_13_plus_years,

      // 6. Specialty Classes (Stud Dog, Brood Bitch, etc.)
      Constants.dog_class_element_level.akc_stud_dog,
      Constants.dog_class_element_level.akc_brood_bitch,
      Constants.dog_class_element_level.akc_brace,
      Constants.dog_class_element_level.akc_coursing,
      Constants.dog_class_element_level.akc_sled_dog,
      Constants.dog_class_element_level.akc_teams,
      Constants.dog_class_element_level.akc_parade_of_titleholders,
      Constants.dog_class_element_level.akc_futurity,
      Constants.dog_class_element_level.akc_best_head,
      Constants.dog_class_element_level.akc_best_movement,
      Constants.dog_class_element_level.akc_best_field,

      // 7. Best of Breed (LAST)
      Constants.dog_class_element_level.akc_best_of_breed,
    ];

    const index = classOrder.indexOf(level);
    return index >= 0 ? index : 9999; // Return 9999 for unknown classes
  };

  const createAKCConformationRunningOrder = async () => {
    let _running_order:IRunningOrder[] = [];

    // Get the actual show elements from the database (props.show.show_elements)
    const actualShowElements = props.show?.show_elements || [];

    // Sort show elements by the defined class order
    const sortedShowElements = [...actualShowElements].sort((a, b) => {
      const orderA = getAKCConformationClassOrder(a.level || '');
      const orderB = getAKCConformationClassOrder(b.level || '');
      return orderA - orderB;
    });

    // Check if this is an all-breed show with breed/group judges
    const hasBreedJudges = bsList && bsList.length > 0;
    const hasBreedGroupJudges = breedGroups && breedGroups.length > 0;
    const isAllBreedShow = !breedSearch || breedSearch.trim() === '';

    if (isAllBreedShow && (hasBreedJudges || hasBreedGroupJudges)) {
      // For all-breed shows with breed/group judges, create separate judge entries

      // Map to track judges and their dog classes
      const judgeMap = new Map<string, IRunningOrder>();

      // Helper function to get or create judge entry
      const getOrCreateJudge = (judgeName: string, judgeNumber: string) => {
        const judgeKey = `${judgeName}-${judgeNumber}`;
        if (!judgeMap.has(judgeKey)) {
          judgeMap.set(judgeKey, {
            judge_name: judgeName,
            judge_number: judgeNumber,
            dog_classes: []
          });
        }
        return judgeMap.get(judgeKey)!;
      };

      // 1. Process breed-specific judges
      if (hasBreedJudges) {
        for (const breedJudge of bsList) {
          try {
            // Get breed information to find the breed group
            const breeds = await apiService.getBreedsBySanctioningClub(props.event?.sanctioning_club || "AKC", {
              query: {
                $and: [
                  {
                    attribute_name: "breed",
                    attribute_value: {
                      operator: "$eq",
                      value: breedJudge.breed
                    }
                  }
                ]
              }
            });

            // Find the breed group for this breed
            let breedGroup = "Guardian"; // fallback
            if (breeds && breeds.length > 0 && breeds[0].groups && breeds[0].groups.length > 0) {
              // Find the group for the current sanctioning club
              const clubGroup = breeds[0].groups.find(g => g.name === (props.event?.sanctioning_club || "AKC"));
              if (clubGroup) {
                breedGroup = normalizeBreedGroupValue(clubGroup.name, clubGroup.value);
              }
            }

            // Add to the appropriate judge
            const judgeEntry = getOrCreateJudge(breedJudge.judge_name, breedJudge.judge_number || "1");
            judgeEntry.dog_classes.push({
              type: Constants.running_order_dog_class_type.dog_class,
              show_element: Constants.dog_class_element.conformation,
              level: `${breedGroup} - ${breedJudge.breed}`
            });
          } catch (error) {
            console.error(`Error fetching breed group for ${breedJudge.breed}:`, error);
            // Fallback - still add to the judge
            const judgeEntry = getOrCreateJudge(breedJudge.judge_name, breedJudge.judge_number || "1");
            judgeEntry.dog_classes.push({
              type: Constants.running_order_dog_class_type.dog_class,
              show_element: Constants.dog_class_element.conformation,
              level: `Guardian - ${breedJudge.breed}`
            });
          }
        }
      }

      // 2. Process breed group judges
      if (hasBreedGroupJudges) {
        for (const breedGroupJudge of breedGroups) {
          if (breedGroupJudge.judge_name && breedGroupJudge.judge_name.trim() !== '') {
            // Add to the appropriate judge
            const judgeEntry = getOrCreateJudge(breedGroupJudge.judge_name, breedGroupJudge.judge_number || "1");

            // Create a single entry for the breed group (expansion happens in backend)
            judgeEntry.dog_classes.push({
              type: Constants.running_order_dog_class_type.dog_class,
              show_element: Constants.dog_class_element.conformation,
              level: breedGroupJudge.breed_group
            });
          }
        }
      }

      // 3. Process show element judges (if any)
      if (judges && judges.length > 0) {
        for (const judge of judges) {
          if (judge.judge_element && judge.judge_element.length > 0) {
            const judgeEntry = getOrCreateJudge(judge.judge_name, judge.judge_number);

            // Add show element entries
            sortedShowElements.forEach((element) => {
              const dogClass: any = {
                type: Constants.running_order_dog_class_type.dog_class,
                show_element: element.show_element,
                level: element.level || ""
              };
              // Include section if present (for C-WAGS Obedience/Rally, UKC Nosework, etc.)
              if (element.section) {
                dogClass.section = element.section;
              }
              judgeEntry.dog_classes.push(dogClass);
            });
          }
        }
      }

      // Convert map to array
      _running_order = Array.from(judgeMap.values());
    } else if (judges.length === 1 && judges[0].judge_element.length === 0) {
      // Using Same Judge (most common for Conformation) - fallback for specialty shows or when no breed/group judges
      let _running_order_item: IRunningOrder = {
        judge_name: judges[0].judge_name,
        judge_number: judges[0].judge_number,
        dog_classes: []
      };

      // Process each show element from the sorted list
      for (const showElement of sortedShowElements) {
        let dc: IRunningOrderDogClass = {
          type: Constants.running_order_dog_class_type.dog_class,
          show_element: showElement.show_element, // Use the actual show_element from DB
          level: showElement.level
        } as any;

        // Include section if present (for C-WAGS Obedience/Rally, UKC Nosework, etc.)
        if (showElement.section) {
          dc.section = showElement.section;
        }

        // Normalize the show_element so ring tiles match how registrations/scores store the class
        // (Sweepstakes / Hunting / Field Trial → "Non-Licensed Classes", Stud Dog / Brood Bitch / Brace → "Conformation").
        if (showElement.level === Constants.dog_class_element.junior_showmanship) {
          dc.show_element = Constants.dog_class_element.junior_showmanship;
          delete dc.level;
        } else {
          dc.show_element = normalizeAKCConformationShowElement(showElement.level, showElement.show_element);
        }

        _running_order_item.dog_classes.push(dc);
      }

      _running_order.push(_running_order_item);
    } else {
      // Multiple judges - process each show element and match to judges
      for (const showElement of sortedShowElements) {
        let hasJudge = _running_order.filter((r: IRunningOrder) => {
          return (r.judge_name === showElement.judge_name && r.judge_number === showElement.judge_number);
        });

        let dc: IRunningOrderDogClass = {
          type: Constants.running_order_dog_class_type.dog_class,
          show_element: showElement.show_element,
          level: showElement.level
        } as any;

        // Include section if present (for C-WAGS Obedience/Rally, UKC Nosework, etc.)
        if (showElement.section) {
          dc.section = showElement.section;
        }

        // Normalize the show_element so ring tiles match how registrations/scores store the class
        // (Sweepstakes / Hunting / Field Trial → "Non-Licensed Classes", Stud Dog / Brood Bitch / Brace → "Conformation").
        if (showElement.level === Constants.dog_class_element.junior_showmanship) {
          dc.show_element = Constants.dog_class_element.junior_showmanship;
          delete dc.level;
        } else {
          dc.show_element = normalizeAKCConformationShowElement(showElement.level, showElement.show_element);
        }

        if (hasJudge.length > 0) {
          hasJudge[0].dog_classes.push(dc);
        } else {
          _running_order.push({
            judge_name: showElement.judge_name,
            judge_number: showElement.judge_number,
            dog_classes: [dc]
          });
        }
      }

      // Sort each judge's dog_classes by the defined order as well
      _running_order.forEach(judge => {
        judge.dog_classes.sort((a, b) => {
          const orderA = getAKCConformationClassOrder(a.level || '');
          const orderB = getAKCConformationClassOrder(b.level || '');
          return orderA - orderB;
        });
      });
    }

    return _running_order;
  }

  // isNonLicensedConformationClass removed (unused)

  const filledTimeslots = () => timeslots.filter((t: ShowRunTimeSlotDtoId) => (t.time_slot || "").trim().length > 0);

  const updateExistingTimeslotsPerSlot = () => {
    const slots = filledTimeslots();
    for (let ots in originalTimeslots) {
      const hasRemovedTimeslot = slots.filter((t: ShowRunTimeSlotDtoId) => originalTimeslots[ots].id === t.id);
      if (hasRemovedTimeslot.length === 0) {
        apiService.deleteShowRunTimeSlot((props.event?._id || ""), (props.show?._id || ""), (originalTimeslots[ots].id || "")).catch((e: any) => {
          toast.show({ title: "Delete Timeslot", content: e?.response?.data?.message || "Something went wrong when deleting a timeslot", duration: 10000, errorDetails: e, type: "fail" });
        });
      }
    }

    for (let ts in slots) {
      if (slots[ts].id !== undefined) {
        const hasTimeslotChanged = originalTimeslots.filter((t: ShowRunTimeSlotDtoId) => t.id === slots[ts].id && JSON.stringify(t) !== JSON.stringify(slots[ts]));
        if (hasTimeslotChanged.length > 0) {
          apiService.updateShowRunTimeSlot((props.event?._id || ""), (props.show?._id || ""), (slots[ts].id || ""), slots[ts]).catch((e: any) => {
            toast.show({ title: "Update Timeslot", content: e?.response?.data?.message || "Something went wrong when updating a timeslot", duration: 10000, errorDetails: e, type: "fail" });
          });
        }
      } else {
        apiService.createShowRunTimeSlot((props.event?._id || ""), (props.show?._id || ""), slots[ts]).catch((e: any) => {
          toast.show({ title: "Create Timeslot", content: e?.response?.data?.message || "Something went wrong when creating a timeslot", duration: 10000, errorDetails: e, type: "fail" });
        });
      }
    }
  }

  const updateExistingTimeslots = () => {
    // Try bulk replace first — preserves order correctly for new shows or shows
    // without any reservations. Falls back to per-slot CRUD if dogs are already
    // registered (bulk replace is blocked once slots are reserved).
    const payload = filledTimeslots().map((t: ShowRunTimeSlotDtoId) => ({
      time_slot: t.time_slot,
      num_available: t.num_available,
      note: t.note,
      level: t.level ?? undefined,
    }));

    apiService.bulkReplaceShowRunTimeSlots((props.event?._id || ""), (props.show?._id || ""), payload).catch((e: any) => {
      const errorCode = e?.response?.data?.eagerdog_error_code;
      if (errorCode === "ERROR.RUN_TIMES.CANNOT_MASS_REPLACE") {
        // Dogs are registered — fall back to per-slot CRUD
        updateExistingTimeslotsPerSlot();
      } else {
        toast.show({
          title: "Update Timeslots",
          content: e?.response?.data?.message || "Something went wrong when saving timeslots.",
          duration: 10000,
          errorDetails: e,
          type: "fail",
        });
      }
    });
  }

  const isSameJudges = (arr1: any[], arr2: any[]): boolean => {
    if (arr1.length !== arr2.length) {
      return false;
    }

    // Normalize arrays to key-value maps for comparison
    const map1 = new Map<string, any>();
    const map2 = new Map<string, any>();

    const createKey = (entry: any) => `${entry.show_element}-${entry.level || ''}-${entry.section || ''}`;

    arr1.forEach(entry => {
      map1.set(createKey(entry), entry);
    });

    arr2.forEach(entry => {
      map2.set(createKey(entry), entry);
    });

    for (const [key, entry1] of map1.entries()) {
      const entry2 = map2.get(key);

      if (!entry2) {
        return false; // Matching key not found
      }

      if (
        entry1.judge_name !== entry2.judge_name ||
        String(entry1.judge_number) !== String(entry2.judge_number)
      ) {
        return false; // Judge data differs
      }
    }

    return true; // All checks passed
  }

  // Editing a Judge Schedules show locks the Judges tab, so a class added here has no way to
  // get a judge before saving, and the API rejects a blank judge_name. Give it a placeholder
  // judge; the club reassigns it in Judge Schedules, which keeps show_elements in sync.
  const fillJudgesForNewClasses = (elements: any[]): any[] => {
    const hasJudge = (j: any) => (j?.judge_name || "").trim() !== "";
    const judged = elements.filter(hasJudge);
    const scheduleJudges = (props.show?.rings || []).flatMap((ring: any) => ring.running_order || []).filter(hasJudge);
    const idleScheduleJudge = scheduleJudges.find((ro: any) => !(ro.dog_classes || []).length);
    const fallback = idleScheduleJudge || judged[0] || scheduleJudges[0];

    return elements.map((se: any) => {
      if (hasJudge(se)) return se;
      const source = judged.find((j: any) => j.show_element === se.show_element) || fallback;
      if (!source) return se;
      return { ...se, judge_name: source.judge_name, judge_number: source.judge_number || "" };
    });
  };

  const createShow = async () => {
    setIsCreatingShow(true);

    const timezone = props.event?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    const showDateWithTZ = moment.tz(moment(showDate).format('YYYY-MM-DD HH:mm'), timezone);

    // For C-WAGS, use nested element setup for Obedience/Rally/Scent
    // For ISWS Conformation, use nested element setup
    // For AKC Obedience/Rally/Conformation, use the standard getShowElements() approach
    const useNestedSetup = shouldUseNestedSetup(showType.id, props.event?.sanctioning_club || '');
    const isUkcRally = isUkcRallyShowForm(props.event?.sanctioning_club, showType.id);
    // Saved shows keep their own class format ("RO1 B" or RO1 + B); new shows are canonical.
    const ukcRallyFormat = getUkcRallyShowFormat(rawProps.show);
    const nestedShowElements = useNestedSetup
      ? getShowElementsByNested(showElementsNested, judges, prices, props.show)
      : [];
    const builtShowElements = useNestedSetup
      ? (isUkcRally ? finaliseUkcRallyShowElements(nestedShowElements, props.show, "canonical") : nestedShowElements)
      : getShowElements();
    const isEditingJudgeScheduleShow = !!props.show && showTypeSupportsJudgeSchedules(props.event?.sanctioning_club || '', showType.id);
    const computedShowElements = isEditingJudgeScheduleShow ? fillJudgesForNewClasses(builtShowElements) : builtShowElements;

    let payload: any = {
      club_name: clubName,
      show_name: showName,
      custom_show_name: customShowName || undefined,
      show_type: showType.value,
      show_date: showDateWithTZ.toISOString(),
      fee: prices.length > 0 ? isNaN(parseInt(prices[0].fee)) ? "" : parseInt(prices[0].fee) : "",
      use_registration_end_date: useCloseDate,
      use_early_bird_fee: useEarlyBirdPrice,
      use_same_fee: useSamePrice,
      show_elements: computedShowElements,
      breed_group_judges: breedGroups.filter((b: BreedGroupJudgeDto) => { 
        // For PWDCA, C-WAGS, and UKC (breed group judges), only judge_name is required.
        // UKC breed group judges commonly don't have a separate judge number for each group.
        if (props.event?.sanctioning_club === Constants.sanctioning_club.PWDCA || 
            props.event?.sanctioning_club === Constants.sanctioning_club.C_WAGS ||
            props.event?.sanctioning_club === Constants.sanctioning_club.UKC) {
          return b.judge_name.length > 0;
        }
        // For other sanctioning clubs, both judge_name and judge_number are required
        return b.judge_name.length > 0 && b.judge_number !== undefined && b.judge_number.length > 0;
      }),
      breed_judges: bsList,
      specialty_breed: breedSearch
    };

    // Only add contacts if user unchecked "use same as event"
    if (!useSameSecretary || !useSameChairperson) {
      const contacts: any[] = [];
      
      if (!useSameSecretary) {
        contacts.push({
          contact_type: "secretary",
          first_name: secFirst,
          last_name: secLast,
          phone: secPhone,
          email: secEmail,
          address: helperService.canEditAddressForContacts(props.event?.sanctioning_club ?? "") ? {
            line1: secretaryAddress,
            line2: secretaryAddress2,
            city: secretaryCity,
            state: secretaryCountry.value === "Canada" ? secretaryProvince.id : secretaryState.id,
            country: secretaryCountry.value,
            postal_code: secretaryZip,
          } : {}
        });
      }
      
      if (!useSameChairperson) {
        contacts.push({
          contact_type: "chairperson",
          first_name: chairFirst,
          last_name: chairLast,
          phone: chairPhone,
          email: chairEmail,
          address: helperService.canEditAddressForContacts(props.event?.sanctioning_club ?? "") ? {
            line1: chairpersonAddress,
            line2: chairpersonAddress2,
            city: chairpersonCity,
            state: chairpersonCountry.value === "Canada" ? chairpersonProvince.id : chairpersonState.id,
            country: chairpersonCountry.value,
            postal_code: chairpersonZip,
          } : {}
        });
      }
      
      payload.contacts = contacts;
    }


    if (props.event?.sanctioning_club === Constants.sanctioning_club.AKC) {
      payload.event_number = akcNum;
      payload.is_fun_match = isFunMatch;
    }

    if (useCloseDate) {
      const closeDateWithTZ = moment.tz(moment(closeDate).format('YYYY-MM-DD HH:mm'), timezone);
      payload.registration_end_date = closeDateWithTZ.toISOString();
    }

    if (isTrailingShow) {
      payload.trailing_show = true;

      if (previousShow) {
        //payload.previous_show_name = previousShow.show_type + " " + previousShow.show_name + " (" + moment(previousShow.show_date).format("MMM Do") + ")";
        payload.previous_show_name = previousShow.show_type + " " + previousShow.show_name;
        payload.previous_show_id = previousShow._id;
        payload.show_date = moment(previousShow.show_date).add(1, 'minutes');
      }
    } else {
      payload.trailing_show = false;
    }

    if (useEarlyBirdPrice) {
      payload.early_bird_end_date = earlyBirdEndDate;
    }

    if (useEarlyBirdPrice && prices.length > 0 && prices[0].early_bird_fee !== undefined) {
      payload.early_bird_fee = parseInt(prices[0].early_bird_fee);
    }

    if (
      props.event?.sanctioning_club === Constants.sanctioning_club.UKC &&
      UKC_JUNIOR_FEE_SHOW_TYPES.includes(showType.id)
    ) {
      payload.junior_fee = juniorFee;
      if (useEarlyBirdPrice) {
        payload.early_bird_junior_fee = earlyBirdJuniorFee;
      }
    }

    // C-WAGS FEO (For Exhibition Only): flag + flat price. Always sent for eligible show
    // types so turning FEO off on edit clears it.
    if (isCwagsFeoEligible(props.event?.sanctioning_club, showType.id)) {
      const feoEnabled = isFeoEnabled();
      payload.feo_available = feoEnabled;
      if (feoEnabled) {
        payload.feo_fee = parseFloat(feoFee);
      }
    }

    if (hasTimeslots.includes(showType.id) && props.show === undefined) {
      payload.time_slots = filledTimeslots();
    } else if (hasTimeslots.includes(showType.id) && props.show !== undefined) {
      updateExistingTimeslots();
    }

    let AKCROSupportedShows:string[] = [
      Constants.show_type.obedience,
      Constants.show_type.rally_obedience,
      Constants.show_type.scentwork,
      Constants.show_type.conformation,
      Constants.show_type.herding,
      Constants.show_type.pre_trial_herding,
      Constants.show_type.instinct_test_herding,
      Constants.show_type.agility,
      Constants.show_type.fast_cat,
      Constants.show_type.fetch,
    ];

    let UKCROSupportedShows:string[] = [
      Constants.show_type.nosework,
      Constants.show_type.obedience,
      Constants.show_type.rally_obedience,
      Constants.show_type.conformation,
      Constants.show_type.shed_dog,
    ];

    let PWDCAROSupportedShows:string[] = [
      Constants.show_type.water_dog,
    ];

    let ABIROSupportedShows:string[] = [
      Constants.show_type.country_rat_race,
    ];

    let GeneralROSupportedShows:string[] = [
      Constants.show_type.sniff_n_go,
      Constants.show_type.dock_diving,
      Constants.show_type.obedience_show_n_go,
      Constants.show_type.general_rally,
    ];

    let CWagsROSupportedShows:string[] = [
      Constants.show_type.scent,
      Constants.show_type.obedience,
      Constants.show_type.rally_obedience,
      Constants.show_type.cwags_games,
    ];

    let BHAROSupportedShows:string[] = [
      Constants.show_type.barn_hunt,
    ];

    let ASFAROSupportedShows:string[] = [
      Constants.show_type.field_trial,
    ];

    if ((props.show === undefined || 
         (props.show !== undefined && (props.show.rings === undefined || props.show.rings.length === 0 || props.show.rings.every((r: any) => !r.running_order || r.running_order.length === 0))) || 
         (originalShowElements.length > 0 && !isSameJudges(originalShowElements, payload.show_elements)) ||
         // UKC Conformation: rebuild whenever no real entries exist so breed-group judge
         // changes (via Edit Show) are always reflected, regardless of ring state.
         (props.event?.sanctioning_club === Constants.sanctioning_club.UKC &&
          showType.value === Constants.show_type.conformation &&
          !props.show?.rings?.some((r: any) =>
            r.running_order?.some((ro: any) =>
              ro.dog_classes?.some((dc: any) => dc.entries && dc.entries > 0)
            )
          ))
        ) && props.event &&
        ((props.event.sanctioning_club === Constants.sanctioning_club.AKC && AKCROSupportedShows.includes(showType.value)) ||
         (props.event.sanctioning_club === Constants.sanctioning_club.UKC && UKCROSupportedShows.includes(showType.value)) ||
         (props.event.sanctioning_club === Constants.sanctioning_club.PWDCA && PWDCAROSupportedShows.includes(showType.value)) ||
         (props.event.sanctioning_club === Constants.sanctioning_club.ABI && ABIROSupportedShows.includes(showType.value)) ||
         (props.event.sanctioning_club === Constants.sanctioning_club.GENERAL && GeneralROSupportedShows.includes(showType.value)) ||
         (props.event.sanctioning_club === Constants.sanctioning_club.C_WAGS && CWagsROSupportedShows.includes(showType.value)) ||
         (props.event.sanctioning_club === Constants.sanctioning_club.BHA && BHAROSupportedShows.includes(showType.value)) ||
         (props.event.sanctioning_club === Constants.sanctioning_club.ASFA && ASFAROSupportedShows.includes(showType.value)))) {
      
      
      payload.rings = [];

      // Check for conformationspecific logic
      const isAKCConformation = props.event.sanctioning_club === Constants.sanctioning_club.AKC &&
                               showType.value === Constants.show_type.conformation;
      const isUKCConformation = props.event.sanctioning_club === Constants.sanctioning_club.UKC &&
                               showType.value === Constants.show_type.conformation;


      if (isAKCConformation) {
        // For AKC Conformation, create rings based on judges
        const runningOrder = await createAKCConformationRunningOrder();
        
        // If there are multiple judges, create separate rings for each
        if (runningOrder.length > 1) {
          runningOrder.forEach((judgeEntry, index) => {
            const ringName = index === 0 ? Constants.ring_names.ring_1 : 
                           index === 1 ? Constants.ring_names.ring_2 :
                           index === 2 ? Constants.ring_names.ring_3 :
                           index === 3 ? Constants.ring_names.ring_4 :
                           `Ring ${index + 1}`;
            
            payload.rings.push({
              ring: ringName,
              running_order: [judgeEntry]
            });
          });
        } else {
          // Single judge or no judges - use Ring 1
          payload.rings.push({
            ring: Constants.ring_names.ring_1,
            running_order: runningOrder
          });
        }
      } else if (isUKCConformation) {
        // bgForRings must include ALL breed groups (assigned AND unassigned) so
        // createUKCConformationRunningOrder can correctly place unassigned groups
        // under the element judge. payload.breed_group_judges is filtered (assigned only)
        // and must NOT be used here for ring building.
        //
        // Priority:
        //   1. breedGroups form state — has all groups; Phase 4 timing fix ensures it's
        //      populated by the time the user saves. Merge in any saved names for groups
        //      whose form inputs were left blank.
        //   2. props.show.breed_group_judges — saved data fallback for edit shows where
        //      form state is somehow still empty.
        //   3. payload.breed_group_judges — last resort (assigned-only, better than nothing).
        let bgForRings: BreedGroupJudgeDto[];
        if (breedGroups.length > 0) {
          // Build a multi-map for saved entries (group may have multiple rows for alpha-split)
          const savedBgMultiMap = new Map<string, BreedGroupJudgeDto[]>();
          for (const bgj of (props.show?.breed_group_judges as BreedGroupJudgeDto[]) ?? []) {
            if (!savedBgMultiMap.has(bgj.breed_group)) savedBgMultiMap.set(bgj.breed_group, []);
            savedBgMultiMap.get(bgj.breed_group)!.push(bgj);
          }
          bgForRings = breedGroups.map((bg: BreedGroupJudgeDto) => {
            // For single-entry groups, fall back to saved judge name if form field is blank
            const savedList = savedBgMultiMap.get(bg.breed_group) ?? [];
            if (savedList.length === 1) {
              const saved = savedList[0];
              if (saved?.judge_name && (!bg.judge_name || bg.judge_name.trim() === '')) {
                return { ...bg, judge_name: saved.judge_name, judge_number: saved.judge_number ?? '', start_letter: saved.start_letter, end_letter: saved.end_letter };
              }
            }
            return bg;
          });
        } else {
          bgForRings =
            (props.show?.breed_group_judges as BreedGroupJudgeDto[])?.length
              ? (props.show?.breed_group_judges as BreedGroupJudgeDto[])
              : ((payload.breed_group_judges as BreedGroupJudgeDto[]) ?? []);
        }
        const runningOrder = createUKCConformationRunningOrder(judges, showElements, bgForRings);
        
        // UKC Conformation: ALL judges (element judge + all breed-group judges) live in
        // Ring 1 as separate running_order entries (judge columns). They are never split
        // across multiple rings — that was a previous bug.
        payload.rings.push({
          ring: Constants.ring_names.ring_1,
          running_order: runningOrder
        });
      } else {
        
        // Standard logic for non-AKC Conformation
        
        // When editing a judge-schedule-managed show, the Judge Schedules tab owns
        // the ring structure entirely. Never rebuild rings here regardless of whether
        // there is real score data — doing so creates duplicates and corrupts the schedule.
        const isJudgeScheduleShow = showTypeSupportsJudgeSchedules(props.event?.sanctioning_club || '', showType.id);

        // When editing: preserve rings if they have real score data (not just placeholders)
        // Placeholders have no entries, no sections - they should be rebuilt
        const hasRealData = props.show?.rings?.some((ring: any) => 
          ring.running_order?.some((ro: any) => 
            ro.dog_classes?.some((dc: any) => 
              dc.entries && dc.entries > 0
            )
          )
        );
        
        if (props.show && isJudgeScheduleShow) {
          // The schedule owns the rings. Read them again so a judge added after
          // this form opened is not replaced by the copy this form started with.
          let rings = props.show.rings ?? [];
          if (props.event?.club_id && props.event._id) {
            try {
              const freshShows = await apiService.getClubEventShows(props.event.club_id, props.event._id);
              const fresh = freshShows?.find((item: IShow) => item._id === props.show?._id);
              if (fresh?.rings) {
                rings = fresh.rings;
              }
            } catch {
              // Keep the rings this form already has.
            }
          }
          payload.rings = rings;
        } else if (props.show && hasRealData) {
          // Preserve rings with real score data - user should use Reset if judges changed
          payload.rings = props.show.rings;
        } else {
          // Create new rings for new shows OR shows with only placeholder rings
          // Use the newly computed payload.show_elements, not the old props.show.show_elements
          let runningOrder = createRunningOrder(payload.show_elements);
          
          // Special handling for Fast CAT events - create running order from show_elements if empty
          if (showType.value === Constants.show_type.fast_cat && runningOrder.length === 0) {
            // Fast CAT events store judge info in show_elements, not judges array
            const fastCatElement = showElements.find((se: any) => se.checked === true);
            if (fastCatElement) {
              // For Fast CAT, always use N/A judge since it doesn't use traditional judges
              runningOrder = [{
                judge_name: "N/A",
                judge_number: "N/A", 
                dog_classes: [{
                  type: Constants.running_order_dog_class_type.dog_class,
                  show_element: showType.id,
                  level: fastCatElement.label
                }]
              }];
            }
          }
          
          // Special handling for Fast Dash events - create running order from show_elements if empty
          if (showType.value === Constants.show_type.fast_dash && runningOrder.length === 0) {
            // Fast Dash events store judge info in show_elements, not judges array
            const fastDashElement = showElements.find((se: any) => se.checked === true);
            if (fastDashElement) {
              // For Fast Dash, always use N/A judge since it doesn't use traditional judges
              runningOrder = [{
                judge_name: "N/A",
                judge_number: "N/A", 
                dog_classes: [{
                  type: Constants.running_order_dog_class_type.dog_class,
                  show_element: showType.id,
                  level: fastDashElement.label
                }]
              }];
            }
          }
          
          // Backward compatibility: Ensure Fast CAT events have proper judge info
          if (showType.value === Constants.show_type.fast_cat && runningOrder.length > 0) {
            runningOrder.forEach(ro => {
              if (!ro.judge_name || ro.judge_name === "") {
                ro.judge_name = "N/A";
                ro.judge_number = "N/A";
              }
            });
          }
          
          // Backward compatibility: Ensure Fast Dash events have proper judge info
          if (showType.value === Constants.show_type.fast_dash && runningOrder.length > 0) {
            runningOrder.forEach(ro => {
              if (!ro.judge_name || ro.judge_name === "") {
                ro.judge_name = "N/A";
                ro.judge_number = "N/A";
              }
            });
          }
          
          payload.rings.push({
            ring: Constants.ring_names.ring_1,
            running_order: runningOrder
          });
        }
      }
    }

    if (armNum) {
      payload.arm_number_start = armNum;
    }

    let isValidShow:boolean = validateShow(payload);

    if (isUkcRally && ukcRallyFormat === "legacy") {
      // Write back in the format the saved show and its scores already use.
      payload.show_elements = payload.show_elements.map((se: any) => toUkcRallyFormat(se, "legacy"));
      payload.rings = convertUkcRallyRingsFormat(payload.rings, "legacy");
    }

    if (props.event && isValidShow) {
      // Filter out rings with empty judge names before saving
      // Backend validation requires all rings to have a judge assigned
      if (payload.rings && payload.rings.length > 0) {
        payload.rings = payload.rings
          .map((ring: any) => ({
            ...ring,
            running_order: (ring.running_order || []).filter(
              (ro: any) => ro.judge_name && ro.judge_name.trim() !== ""
            ),
          }))
          .filter((ring: any) => ring.running_order && ring.running_order.length > 0);
      }

      if (props.show) {
        apiService.updateShow(props.event._id, props.show._id, payload).then((response) => {
          if (response._id) {
            props.onFinish();
          }
        }).catch((e: AxiosError) => {
          const messages: string[] = (e.response?.data as any)?.message ?? [];
          const missingJudges = Array.isArray(messages) && messages.some((m: string) => m.includes("judge_name"));
          toast.show({
            title: "Edit show",
            content: missingJudges
              ? "Some classes are missing judge assignments. Please fill in all judge names before saving."
              : (Array.isArray(messages) && messages.length > 0 ? messages[0] : "Unable to edit show"),
            duration: 10000,
            errorDetails: e,
            type: "fail",
          });
        }).finally(() => {
          setIsCreatingShow(false);
        });
      } else {
        apiService.createShow(props.event._id, payload).then((response) => {
          if (response._id) {
            props.onFinish();
          }
        }).catch((e: AxiosError) => {
          const errorMessage = (e.response?.data as any)?.message || "Unable to create show";
          toast.show({
            title: "Create show",
            content: errorMessage,
            duration: 10000,
            type: "fail",
          });
        }).finally(() => {
          setIsCreatingShow(false);
        });
      }
    } else {
      setIsCreatingShow(false);
    }
  }

  const pShowType = helperService.usePrevious(showType);

  useEffect(() => {
    if (showType !== pShowType) {
      if (hasTimeslots.includes(showType.id)) {
        setSteps(["Show Type", "Show Name and Date", "Judge", "Price", "Secretary (optional)", "Chairperson (optional)"/*, "Waitlist"*/, showType.id === Constants.show_type.fetch ? "Run Times (optional)" : "Run Times"]);
      } else {
        setSteps(["Show Type", "Show Name and Date", "Judge", "Price", "Secretary (optional)", "Chairperson (optional)"/*, "Waitlist"*/]);
      }
    }
  }, [showType, pShowType, hasTimeslots]);

  // Initialize splash count from existing show data
  useEffect(() => {
    if (props.show) {
      const airElement = props.show.show_elements.find(element =>
        element.show_element === Constants.dog_class_element.dock_jumping_air
      );
      if (airElement && airElement.splash_count) {
        setSplashCount(airElement.splash_count);
      }
    }
  }, [props.show]);

  return (
    <div className="ShowForm">
      <div className="title">{props.show ? "Edit Show": "Create a Show"}</div>
      <div className="formWrap">
        <StepForm activeTab={activeTab} next={switchToTab} steps={steps} onSubmit={createShow} isLoading={isCreatingShow}>
          <Step>
            <>{props.event && <ShowTypeTab
              event={props.event}
              show={props.show !== undefined ? props.show : undefined}
              onChange={(clubName: string, newShowType: IOption, newShowElements: ICheck[], elementJudges: ICheck[], armNum: number, breedSearch: string, splashCount?: number, showElementsNested?: INestedShowElement[], _feoAvailable?: boolean) => {
                setClubName(clubName);
                setShowType(newShowType);
                setShowElements(newShowElements);
                setShowElementJudges(elementJudges);
                setArmNum(armNum);
                setBreedSearch(breedSearch);
                if (splashCount !== undefined) {
                  setSplashCount(splashCount);
                }
                if (!!showElementsNested?.length) {
                  setShowElementsNested(showElementsNested);
                }
                if (_feoAvailable !== undefined) {
                  setFeoShowWideAvailable(_feoAvailable);
                }
              }}
            />}</>
          </Step>
          <Step>
            <>{props.event && <ShowNameTab
              event={props.event}
              show={props.show !== undefined ? props.show : undefined}
              showType={showType.id}
              clubId={props.clubId}
              onChange={(_akcNum: string, _showName: string, _showDate: any, _closeDate: any, _useCloseDate: boolean, _isTrailingShow: boolean, _previousShow: IShow, _customShowName: string, _isFunMatch: boolean) => {
                setAkcNum(_akcNum);
                setShowName(_showName);
                setCustomShowName(_customShowName);
                setShowDate(_showDate);
                setCloseDate(_closeDate);
                setUseCloseDate(_useCloseDate);
                setIsTrailingShow(_isTrailingShow);
                setPreviousShow(_previousShow);
                setIsFunMatch(_isFunMatch);
              }}
            />}</>
          </Step>
          <Step>
            <>{props.event && <JudgesTab
              event={props.event}
              show={props.show !== undefined ? props.show : undefined}
              showType={showType.id}
              showElements={showElements}
              showElementsNested={showElementsNested}
              specialtyBreed={breedSearch}
              readOnly={!!props.show && !!props.event && showTypeSupportsJudgeSchedules(props.event.sanctioning_club, showType.id)}
              onNavigateToJudgeSchedules={props.onNavigateToJudgeSchedules}
              onChange={(_judges: IJudge[], _useSameJudge: boolean, _breedGroups: BreedGroupJudgeDto[], _breedSpecific: BreedJudgeDto[]) => {
                setJudges(_judges);
                setUseSameJudge(_useSameJudge);
                setBreedGroups(_breedGroups);
                setBsList(_breedSpecific);
              }}
            />}</>
          </Step>
          <Step>
            <>{props.event && <PriceTab
              event={props.event}
              show={props.show !== undefined ? props.show : undefined}
              currency={props.currency ? props.currency : undefined}
              showType={showType.id}
              showElements={showElements}
              showElementsNested={showElementsNested}
              feoAvailable={isFeoEnabled()}
              onChange={(
                _prices,
                _useSamePrice,
                _useEarlyBird,
                _earlyBirdEndDate,
                _juniorFee,
                _earlyBirdJuniorFee,
                _feoFee
              ) => {
                setPrices(_prices);
                setUseSamePrice(_useSamePrice);
                setUseEarlyBirdPrice(_useEarlyBird);
                setEarlyBirdEndDate(_earlyBirdEndDate);
                setJuniorFee(_juniorFee);
                setEarlyBirdJuniorFee(_earlyBirdJuniorFee);
                setFeoFee(_feoFee);
              }}
            />}</>
          </Step>
          <Step>
            <div className="stepTitle">Secretary</div>
            <p className="optional-question">Who is the secretary for this show?</p>
            <div className="optional-info-box">
              This is an optional step. Only use this if the secretary for this show is different than the event secretary.
            </div>
            <Checkbox 
              onChange={() => setUseSameSecretary(!useSameSecretary)} 
              value={useSameSecretary} 
              id="useSameSecretary" 
              label="Use same Secretary as the Event Secretary" 
            />
            {!useSameSecretary ? (
              <>
                <Input defaultValue={secFirst} label="First Name" id="secFirst" type="text" onChange={(e) => { e.stopPropagation(); setSecFirst(e.target.value); }} placeholder="What's the secretary's first name?" />
                <Input defaultValue={secLast} label="Last Name" id="secLast" type="text" onChange={(e) => { e.stopPropagation(); setSecLast(e.target.value); }} placeholder="What's the secretary's last name?" />
                <Phone label="Phone Number" value={secPhone} onChange={setSecPhone} />
                <Input defaultValue={secEmail} label="Email" id="secEmail" type="email" onChange={(e) => { e.stopPropagation(); setSecEmail(e.target.value); }} placeholder="What's your secretary's email?" />
                {helperService.canEditAddressForContacts(props.event?.sanctioning_club ?? "") ? (
                  <>
                    <p>Where is your secretary located?</p>
                    <div className="half">
                      <Input label="Address Line 1 or Name of Location" id="secretaryAddress" defaultValue={secretaryAddress} type="text" onChange={(e) => { e.stopPropagation(); setSecretaryAddress(e.target.value); }} placeholder="What's secretary's address?" />
                      <Input label="Address Line 2" id="secretaryAddress2" type="text" defaultValue={secretaryAddress2} onChange={(e) => { e.stopPropagation(); setSecretaryAddress2(e.target.value); }} placeholder=""/>
                    </div>
                    <div className="half">
                      <Input label="City" id="secretaryCity" defaultValue={secretaryCity} type="text" onChange={(e) => { e.stopPropagation(); setSecretaryCity(e.target.value); }} />
                      <Input label="Postal/ZIP Code" id="secretaryZip" defaultValue={secretaryZip} type="text" onChange={(e) => { e.stopPropagation(); setSecretaryZip(e.target.value); }} />
                    </div>
                    <div className="half">
                      <Dropdown value={secretaryCountry} onChange={(e: any, value: IOption) => { setSecretaryCountry(value); }} icon={flagIcon} label="Country" options={[ { value: "Canada", id: "Canada" }, { value: "USA", id: "USA" } ]} placeholder="Choose secretary's country of residence" />
                      {secretaryCountry.id === "USA" ? (<Dropdown value={secretaryState} onChange={(e: any, value: IOption) => { setSecretaryState(value); }} icon={pinIcon} label="State" options={States} placeholder="Choose secretary's state" />) : null}
                      {secretaryCountry.id === "Canada" ? (<Dropdown value={secretaryProvince} onChange={(e: any, value: IOption) => { setSecretaryProvince(value); }} icon={pinIcon} label="Province / State" options={Provinces} alignRight={true} placeholder="Choose secretary's province" />) : null}                    </div>
                  </>
                ) : <></>}
              </>
            ) : <></>}
          </Step>
          <Step>
            <div className="stepTitle">Chairperson</div>
            <p className="optional-question">Who is the chairperson for this show?</p>
            <div className="optional-info-box">
              This is an optional step. Only use this if the chairperson for this show is different than the event chairperson.
            </div>
            <Checkbox 
              onChange={() => setUseSameChairperson(!useSameChairperson)} 
              value={useSameChairperson} 
              id="useSameChairperson" 
              label="Use same Chairperson as the Event Chairperson" 
            />
            {!useSameChairperson ? (
              <>
                <Input defaultValue={chairFirst} label="First Name" id="chairFirst" type="text" onChange={(e) => { e.stopPropagation(); setChairFirst(e.target.value); }} placeholder="What's the chairperson's first name?" />
                <Input defaultValue={chairLast} label="Last Name" id="chairLast" type="text" onChange={(e) => { e.stopPropagation(); setChairLast(e.target.value); }} placeholder="What's the chairperson's last name?" />
                <Phone label="Phone Number" value={chairPhone} onChange={setChairPhone} />
                <Input defaultValue={chairEmail} label="Email" id="chairEmail" type="email" onChange={(e) => { e.stopPropagation(); setChairEmail(e.target.value); }} placeholder="What's your chairperson's email?" />
                {helperService.canEditAddressForContacts(props.event?.sanctioning_club ?? "") ? (
                  <>
                    <p>Where is your chairperson located?</p>
                    <div className="half">
                      <Input label="Address Line 1 or Name of Location" id="chairpersonAddress" defaultValue={chairpersonAddress} type="text" onChange={(e) => { e.stopPropagation(); setChairpersonAddress(e.target.value); }} placeholder="What's chairperson's address?" />
                      <Input label="Address Line 2" id="chairpersonAddress2" type="text" defaultValue={chairpersonAddress2} onChange={(e) => { e.stopPropagation(); setChairpersonAddress2(e.target.value); }} placeholder=""/>
                    </div>
                    <div className="half">
                      <Input label="City" id="chairpersonCity" defaultValue={chairpersonCity} type="text" onChange={(e) => { e.stopPropagation(); setChairpersonCity(e.target.value); }} />
                      <Input label="Postal/ZIP Code" id="chairpersonZip" defaultValue={chairpersonZip} type="text" onChange={(e) => { e.stopPropagation(); setChairpersonZip(e.target.value); }} />
                    </div>
                    <div className="half">
                      <Dropdown value={chairpersonCountry} onChange={(e: any, value: IOption) => { setChairpersonCountry(value); }} icon={flagIcon} label="Country" options={[ { value: "Canada", id: "Canada" }, { value: "USA", id: "USA" } ]} placeholder="Choose chairperson's country of residence" />
                      {chairpersonCountry.id === "USA" ? (<Dropdown value={chairpersonState} onChange={(e: any, value: IOption) => { setChairpersonState(value); }} icon={pinIcon} label="State" options={States} placeholder="Choose chairperson's state" />) : null}
                      {chairpersonCountry.id === "Canada" ? (<Dropdown value={chairpersonProvince} onChange={(e: any, value: IOption) => { setChairpersonProvince(value); }} icon={pinIcon} label="Province / State" options={Provinces} alignRight={true} placeholder="Choose chairperson's province" />) : null}                    </div>
                  </>
                ) : <></>}
              </>
            ) : <></>}
          </Step>
          {/*<Step>
            <>{props.event && <WaitlistTab
              event={props.event}
              showType={showType.id}
              show={props.show !== undefined ? props.show : undefined}
              showElements={showElements}
              onChange={(newWaitlists: IWaitlistItem[]) => {
                setWaitlists(newWaitlists);
              }}
            />}</>
          </Step>*/}
          <>{(hasTimeslots.includes(showType.id)) && <Step>
            <TimeslotTab
              show={props.show !== undefined ? props.show : undefined}
              clubId={props.clubId}
              showType={showType.id}
              availableLevels={showElements.filter((se: ICheck) => se.checked).map((se: ICheck) => se.label)}
              onChange={(timeslots: ShowRunTimeSlotDtoId[], originalTimeslots: ShowRunTimeSlotDtoId[]) => {
                setTimeslots(timeslots);
                setOriginalTimeslots(originalTimeslots);
              }}
            />
          </Step>}</>
        </StepForm>
      </div>
    </div>
  );
};

export default ShowForm;